import { type Profession, UserAction } from "@brookbush/types";

import { useAuth, useUser } from "@clerk/nextjs";
import { Dialog, DialogPanel } from "@headlessui/react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { useEffect, useState } from "react";

import Button from "./Button";
import SelectAProfessionDisclaimer from "./SelectAProfessionDisclaimer";
import SelectAProfessionField from "./SelectAProfessionField";
import TextField from "./TextField";
import { useProfessions, useSelectedProfessions, useUserAction } from "~/hooks";

const SelectAProfessionModal = () => {
	const { userAction, resetUserAction } = useUserAction();

	const { userId } = useAuth();
	const { user } = useUser();

	const isOpen = userAction === UserAction.SELECT_A_PROFESSION && !!userId;

	const { professions } = useProfessions();

	const {
		selectedProfessions,
		handleChangeProfession,
		isLoading: isLoadingSelectedProfessions,
	} = useSelectedProfessions();

	const [primaryProfession, setPrimaryProfession] = useState<Profession | null>(
		null,
	);
	const [secondaryProfession, setSecondaryProfession] =
		useState<Profession | null>(null);
	const [showDisclaimer, setShowDisclaimer] = useState(false);
	const [isDisclaimerAccepted, setIsDisclaimerAccepted] = useState(false);
	const [cataMemberNumber, setCataMemberNumber] = useState<string>(
		(user?.unsafeMetadata.cataMemberNumber as string) || "",
	);

	const isChanged =
		primaryProfession?.slug !== selectedProfessions?.primaryProfession?.slug ||
		secondaryProfession?.slug !==
			selectedProfessions?.secondaryProfession?.slug;

	const requiresCataMemberNumber =
		primaryProfession?.slug === "certified-athletic-therapist" ||
		secondaryProfession?.slug === "certified-athletic-therapist";

	const canSubmit =
		primaryProfession &&
		isChanged &&
		(requiresCataMemberNumber ? cataMemberNumber : true) &&
		(showDisclaimer ? isDisclaimerAccepted : true);

	useEffect(() => {
		if (selectedProfessions) {
			selectedProfessions.primaryProfession &&
				setPrimaryProfession(selectedProfessions.primaryProfession);
			selectedProfessions.secondaryProfession &&
				setSecondaryProfession(selectedProfessions.secondaryProfession);
		}
	}, [selectedProfessions]);

	useEffect(() => {
		if (
			primaryProfession?.slug === "occupational-therapy" ||
			secondaryProfession?.slug === "occupational-therapy"
		) {
			setShowDisclaimer(true);
		} else {
			setShowDisclaimer(false);
			setIsDisclaimerAccepted(false);
		}
	}, [primaryProfession, secondaryProfession]);

	const isLoading = !professions || isLoadingSelectedProfessions;

	const handleClose = () => {
		resetUserAction();
	};

	return (
		<AnimatePresence>
			{isOpen && !isLoading && (
				<Dialog
					key="profession-dialog"
					as={motion.div}
					static
					open={isOpen && !isLoading}
					onClose={handleClose}
					className="relative z-50"
				>
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1, transition: { duration: 0.3 } }}
						exit={{ opacity: 0, transition: { duration: 0.2 } }}
						className="fixed inset-0 z-50 bg-black/40 dark:bg-black/50"
						aria-hidden="true"
						key="select-a-profession-backdrop"
					/>

					<div className="fixed inset-0 z-50 overflow-y-auto">
						<form
							className="flex min-h-full items-end justify-center sm:items-center sm:p-4 lg:p-10"
							onSubmit={async (e) => {
								e.preventDefault();

								try {
									await handleChangeProfession({
										primaryProfession,
										secondaryProfession,
										cataMemberNumber: requiresCataMemberNumber
											? cataMemberNumber
											: undefined,
									});
									handleClose();
								} catch {
									// Toast already surfaced the failure; keep the modal open.
								}
							}}
						>
							<DialogPanel
								as={motion.div}
								initial={{ opacity: 0, y: 15 }}
								animate={{ opacity: 1, y: 0, transition: { duration: 0.2 } }}
								exit={{ opacity: 0, y: 15, transition: { duration: 0.15 } }}
								className="relative mx-auto flex w-full max-w-md flex-col gap-6 rounded-t-xl bg-white px-4 pt-4 pb-10 shadow-2xl shadow-black/10 sm:gap-5 sm:rounded-2xl sm:px-8 sm:pt-6 sm:pb-8 dark:bg-slate-800 dark:shadow-slate-900"
							>
								<div className="flex items-center justify-between">
									<Dialog.Title className="font-semibold phablet:text-2xl text-xl">
										Select Your Profession
									</Dialog.Title>
									<button
										className="relative right-0 flex cursor-pointer rounded-full bg-slate-100 p-1 transition-all hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600"
										onClick={handleClose}
									>
										<X className="h-5 phablet:h-6 phablet:w-6 w-5" />
									</button>
								</div>

								<div className="flex flex-col gap-6 sm:gap-6 sm:py-4">
									<SelectAProfessionField
										value={primaryProfession}
										onChange={setPrimaryProfession}
										filterFn={(p) => p.slug !== secondaryProfession?.slug}
										label="Primary:"
										id="primary-profession"
										isModal
										placeholder="Select a profession..."
									/>

									<SelectAProfessionField
										value={secondaryProfession}
										onChange={setSecondaryProfession}
										filterFn={(p) => p.slug !== primaryProfession?.slug}
										label="Secondary:"
										id="secondary-profession"
										isModal
										placeholder="Optional"
										disabled={!primaryProfession}
									/>

									{requiresCataMemberNumber && (
										<div className="flex flex-col gap-2">
											<label
												htmlFor="cata-member-number"
												className="font-medium text-slate-700 text-sm dark:text-slate-200"
											>
												CATA Member Number
											</label>

											<TextField
												type="text"
												id="cata-member-number"
												value={cataMemberNumber}
												onChange={(e) => setCataMemberNumber(e.target.value)}
												placeholder="Enter your CATA member number"
											/>
										</div>
									)}
								</div>

								{showDisclaimer && (
									<SelectAProfessionDisclaimer
										disclaimer="In accordance with AOTA guidelines, to achieve satisfactory completion of all courses approved by the AOTA, you must reflect on all completed course materials and how they apply to your specific practice. If upon reflection you have any question(s), we advise you to leave your question(s) in the comment box at the bottom of the course. Questions are generally answered within 24 hours. Any question specific to the OT practice will be answered by a licensed OT with significant (10+ years) clinical experience."
										isDisclaimerAccepted={isDisclaimerAccepted}
										setIsDisclaimerAccepted={setIsDisclaimerAccepted}
									/>
								)}

								<Button variant={"filled"} disabled={!canSubmit} type="submit">
									Confirm Changes
								</Button>
							</DialogPanel>
						</form>
					</div>
				</Dialog>
			)}
		</AnimatePresence>
	);
};

export default SelectAProfessionModal;
